import React, { useCallback, useEffect, useMemo, useState } from 'react';

const RANGE_OPTIONS = [
  { value: 'all', label: '全期間' },
  { value: '7', label: '直近7日' },
  { value: '30', label: '直近30日' },
  { value: '90', label: '直近90日' },
];

const DEVICE_LABELS = {
  desktop: 'デスクトップ',
  mobile: 'モバイル',
  tablet: 'タブレット',
  unknown: '不明',
};

function formatNumber(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat('ja-JP').format(Number.isFinite(num) ? num : 0);
}

function AnalyticsTable({ title, rows, columns }) {
  return (
    <div className="analytics-table-card">
      <h4>{title}</h4>
      <table className="analytics-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="analytics-empty">データがありません</td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={row.key || row.id || `${row[columns[0].key]}-${row.total}`}>
              {columns.map((column) => (
                <td key={column.key}>{row[column.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnalyticsDashboard() {
  const [range, setRange] = useState('all');
  const [mode, setMode] = useState('total');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/analytics/summary?range=${range}&mode=${mode}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('failed to load analytics');
      }
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      console.error(err);
      setError('ダッシュボードの読み込みに失敗しました');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [range, mode]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const maxDailyValue = useMemo(() => {
    if (!summary || !summary.daily || !summary.daily.length) {
      return 0;
    }
    return summary.daily.reduce((max, row) => Math.max(max, row.total), 0);
  }, [summary]);

  const countLabel = mode === 'unique' ? 'ユニーク訪問者数' : 'アクセス数';
  const dailyTitle = mode === 'unique' ? '日別ユニーク訪問者数' : '日別アクセス数';

  return (
    <div className="analytics-panel">
      <div className="analytics-toolbar">
        <div>
          <label>
            期間
            <select value={range} onChange={(event) => setRange(event.target.value)}>
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <button type="button" onClick={fetchSummary} disabled={loading}>
            再読み込み
          </button>
        </div>
        <p className="analytics-note">全期間または最長120日まで集計できます</p>
      </div>
      <div className="analytics-mode-tabs">
        <button
          type="button"
          className={mode === 'total' ? 'analytics-tab-active' : 'analytics-tab'}
          onClick={() => setMode('total')}
        >
          総アクセス数
        </button>
        <button
          type="button"
          className={mode === 'unique' ? 'analytics-tab-active' : 'analytics-tab'}
          onClick={() => setMode('unique')}
        >
          ユニーク訪問者数
        </button>
      </div>
      {loading && <p>読み込み中...</p>}
      {error && !loading && <p className="admin-error">{error}</p>}
      {!loading && !error && summary && (
        <>
          <div className="analytics-grid">
            <div className="analytics-card">
              <p className="analytics-label">
                {mode === 'unique' ? 'ユニーク訪問者数' : '総アクセス数'}
              </p>
              <p className="analytics-value">
                {formatNumber(mode === 'unique' ? summary.totals?.uniqueVisitors : summary.totals?.events)}
              </p>
            </div>
            <div className="analytics-card">
              <p className="analytics-label">ユニークページ数</p>
              <p className="analytics-value">{formatNumber(summary.totals?.uniquePages)}</p>
            </div>
          </div>

          <div className="analytics-grid">
            <AnalyticsTable
              title="人気ページ"
              columns={[
                { key: 'path', label: 'パス' },
                { key: 'title', label: 'タイトル' },
                { key: 'total', label: countLabel },
              ]}
              rows={(summary.topPages || []).map((row) => ({
                ...row,
                key: row.path,
                path: row.path || '(未設定)',
                title: row.title || '(タイトルなし)',
                total: formatNumber(row.total),
              }))}
            />
            <AnalyticsTable
              title="国別"
              columns={[
                { key: 'country', label: '国' },
                { key: 'total', label: countLabel },
              ]}
              rows={(summary.topCountries || []).map((row) => {
                const label = row.country && row.country !== 'UNKNOWN' ? row.country : '不明';
                return {
                  ...row,
                  country: label,
                  key: row.country || 'unknown',
                  total: formatNumber(row.total),
                };
              })}
            />
            <AnalyticsTable
              title="ブラウザ"
              columns={[
                { key: 'browser', label: 'ブラウザ' },
                { key: 'total', label: countLabel },
              ]}
              rows={(summary.topBrowsers || []).map((row) => ({
                ...row,
                browser: row.browser || '不明',
                key: row.browser || 'unknown',
                total: formatNumber(row.total),
              }))}
            />
            <AnalyticsTable
              title="デバイス"
              columns={[
                { key: 'device', label: 'デバイス' },
                { key: 'total', label: countLabel },
              ]}
              rows={(summary.devices || []).map((row) => ({
                ...row,
                key: row.device || 'unknown',
                device: DEVICE_LABELS[row.device] || row.device || '不明',
                total: formatNumber(row.total),
              }))}
            />
          </div>

          <div className="analytics-timeseries">
            <h4>{dailyTitle}</h4>
            {(!summary.daily || !summary.daily.length) && (
              <p className="analytics-empty">まだデータがありません</p>
            )}
            {(summary.daily || []).map((row) => {
              const width = maxDailyValue ? Math.max((row.total / maxDailyValue) * 100, 4) : 0;
              return (
                <div className="analytics-timeseries-row" key={row.day}>
                  <span className="analytics-timeseries-date">{row.day}</span>
                  <div className="analytics-timeseries-bar">
                    <span style={{ width: `${width}%` }} />
                  </div>
                  <span className="analytics-timeseries-value">{formatNumber(row.total)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
